/**
 * SlashCommands -- Registry de comandos slash para activar action-area sin pasar por n8n.
 *
 * Patron explicit-trigger (no detecta intent automatico). El usuario activa el modo
 * Watcher (toggle) y escribe comandos como `/factura`, `/diseno` para invocar
 * directamente un modo del ActionArea, sin enviar el mensaje al agente.
 *
 * Por que asi?
 *   - Predecible: el usuario sabe exactamente que pasa con cada comando
 *   - Sin riesgo de Argemiro adivinando mal (anti-pattern observado en Chat/Consulta/RAG)
 *   - Apto para voz futura: mic 2 transcribe -> mapea al keyword -> invoca comando
 *   - Coexiste con chat tradicional: si toggle Watcher OFF, los `/` se envian como texto normal
 *
 * Uso:
 *   import { parseSlashCommand, getSlashCommandRegistry } from '../components/SlashCommands.js';
 * (renderSlashHelp, la ayuda en HTML, se retiro: nadie la montaba -- obs de Q #36725. Si hace falta, se arma desde
 * getSlashCommandRegistry.)
 *   const cmd = parseSlashCommand('/factura sube esta');
 *   if (cmd) actionArea.showMode(cmd.targetMode, cmd.payload);
 *
 * @module presentation/components/SlashCommands
 */

const VERSION = 'v1.0';

/**
 * Registry de comandos disponibles. Ampliable sin tocar logica del parser.
 * Cada comando define:
 *   - alias: string[] que invocan el comando (case-insensitive, sin slash)
 *   - description: para help text
 *   - targetMode: el modo del ActionArea que invoca
 *   - payloadFactory(args, context): devuelve el payload para showMode
 */
const REGISTRY = [
  {
    alias: ['factura', 'compra', 'recibo'],
    description: 'Sube una factura para procesar (compra)',
    targetMode: 'inline-upload',
    icon: '🧾',
    payloadFactory: (args) => ({
      label: '🧾 Sube tu factura',
      accept: 'image/png,image/jpeg,image/webp,application/pdf',
      maxSize: 10 * 1024 * 1024,
      onSend: (files) => {
        // KIOSK-ARGEMIRO-COMPRA-V1 (T4): en ArgemiroKioskView el comando /factura se
        // intercepta con parseSlashCommand y abre el capturador de foto directamente
        // (-> ComprasApiClient.uploadOcr). Este onSend queda como hook para consumidores
        // que rendericen el comando via ActionArea inline-upload.
        console.info('[SlashCommands] /factura onSend', files);
      }
    })
  },
  {
    // KIOSK-DESPERDICIO-V1 (bit #3021), renombrado a Movimientos en MK-T1 (bit #3257):
    // mismo patron de credibilidad que /factura -- ArgemiroKioskView intercepta este
    // alias ANTES de llegar al ActionArea (stepper conversacional propio:
    // insumo -> tipo -> cantidad -> nota -> confirmar). tipo ahora cubre
    // desperdicio/dano/aumento (MV-T2 bit #3349: codes canonicos; 'merma' se
    // conserva SOLO como alias de escritura por costumbre del operador).
    alias: ['desperdicio', 'merma', 'dano', 'movimiento', 'aumento'],
    description: 'Registra un movimiento de insumo: desperdicio, dano o aumento',
    targetMode: 'inline-desperdicio',
    icon: '📦',
    payloadFactory: () => null // manejado por ArgemiroKioskView, no via ActionArea
  },
  {
    alias: ['diseno', 'design', 'logo'],
    description: 'Genera un diseño desde una imagen de referencia',
    targetMode: 'inline-upload',
    icon: '🎨',
    payloadFactory: (args) => ({
      label: '🎨 Imagen de referencia para el diseño',
      accept: 'image/png,image/jpeg,image/webp',
      maxSize: 5 * 1024 * 1024,
      onSend: (files) => {
        console.info('[SlashCommands] /diseno onSend', files);
        // Hook para T4: endpoint diseno
      }
    })
  },
  {
    alias: ['onboarding', 'nuevo-negocio', 'registrar-negocio'],
    description: 'Wizard para registrar un negocio nuevo (5 pasos)',
    targetMode: 'table-results', // placeholder hasta que T2 stepper-flow este listo
    icon: '🚀',
    payloadFactory: (args) => ({
      title: '🚀 Onboarding Negocio Nuevo',
      headers: ['Paso', 'Campo', 'Estado'],
      rows: [
        { Paso: '1', Campo: 'Tipo + Nombre',          Estado: 'Pendiente (T5 ACTION-AREA)' },
        { Paso: '2', Campo: 'NIT/RUT + Municipio',     Estado: 'Pendiente (T5 ACTION-AREA)' },
        { Paso: '3', Campo: 'Moneda + Timezone',       Estado: 'Pendiente (T5 ACTION-AREA)' },
        { Paso: '4', Campo: 'Primer admin user',       Estado: 'Pendiente (T5 ACTION-AREA)' },
        { Paso: '5', Campo: 'Confirmar + crear',       Estado: 'Pendiente (T5 ACTION-AREA)' }
      ]
    })
  },
  {
    alias: ['aprende-descarga', 'descarga-desktop', 'instalar'],
    description: 'Capacitación: cómo descargar e instalar Nexo Desktop',
    targetMode: 'table-results', // placeholder hasta T6 stepper multimedia
    icon: '📚',
    payloadFactory: (args) => ({
      title: '📚 Aprende: Descargar Nexo Desktop',
      headers: ['Paso', 'Tema', 'Tipo'],
      rows: [
        { Paso: '1', Tema: 'Ir a nexodigital360.com → Descarga',  Tipo: 'imagen + texto (T6)' },
        { Paso: '2', Tema: 'Click botón verde "Descargar"',       Tipo: 'imagen + texto (T6)' },
        { Paso: '3', Tema: 'Instalación ClickOnce primer arranque', Tipo: 'video corto (T6)' },
        { Paso: '4', Tema: 'Pedir ayuda remota TeamViewer',       Tipo: 'imagen + texto (T6)' },
        { Paso: '5', Tema: 'Quiz validación 3 preguntas',          Tipo: 'quiz embedded (T6)' }
      ]
    })
  },
  {
    alias: ['help', 'ayuda', '?'],
    description: 'Lista de comandos disponibles',
    targetMode: '__help__',
    icon: '❓',
    payloadFactory: () => null // manejado especial
  }
];

/**
 * Parsea un texto de input. Si empieza con `/` Y matchea un alias del registry,
 * devuelve el comando + args. Si no, devuelve null (el panel manda el texto a n8n normal).
 *
 * @param {string} text - input completo del usuario (puede tener trailing spaces)
 * @returns {object|null} { command, alias, args, targetMode, payload } o null
 */
export function parseSlashCommand(text) {
  if (!text || typeof text !== 'string') return null;
  const trimmed = text.trim();
  if (!trimmed.startsWith('/')) return null;

  const parts = trimmed.slice(1).split(/\s+/);
  const aliasRaw = (parts[0] || '').toLowerCase();
  if (!aliasRaw) return null;

  const args = parts.slice(1).join(' ');

  for (const cmd of REGISTRY) {
    if (cmd.alias.includes(aliasRaw)) {
      return {
        command: cmd,
        alias: aliasRaw,
        args,
        targetMode: cmd.targetMode,
        payload: cmd.payloadFactory(args, { alias: aliasRaw })
      };
    }
  }
  return null;
}

/**
 * Devuelve el registry crudo (para tooltip/help dinamico).
 */
export function getSlashCommandRegistry() {
  return REGISTRY.map(c => ({
    primary: c.alias[0],
    aliases: c.alias,
    description: c.description,
    icon: c.icon
  }));
}

/**
 * Helper: ¿el toggle Watcher esta activo?
 * El estado vive en localStorage para persistir entre sesiones.
 */
const WATCHER_KEY = 'nx_watcher_mode';
export function isWatcherActive() {
  return localStorage.getItem(WATCHER_KEY) === '1';
}
export function setWatcherActive(active) {
  localStorage.setItem(WATCHER_KEY, active ? '1' : '0');
}

export const SLASH_COMMANDS_VERSION = VERSION;
